import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PlusIcon } from '../../../../assets/icons/element-plus-icon.svg';
import { Amenity } from '../../../../interface/partner-interfaces/station-management.interface';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import formatToCamelCase from '../../../../shared-functions/formatToCamelCase';

import SelectAmenitiesV2 from './SelectAmenitiesV2';

interface AmenitiesInputProperties {
  selectedAmenities: Amenity[];
  handleStationDetailChange: (name: string, changeValue: Amenity[]) => void;
  tooltipText?: string;
  dynamicAmenities?: Amenity[];
  isLoading?: boolean;
}

/**
 * Amenities input with dropdown to select amenities
 * @param {any} param0 - The param0
 * @param {any} param0.selectedAmenities - The selectedAmenities prop
 * @param {any} param0.handleStationDetailChange - The handleStationDetailChange prop
 * @param {any} param0.tooltipText - The tooltipText prop
 * @param {any} param0.dynamicAmenities - The dynamicAmenities prop
 * @param {any} param0.isLoading - The isLoading prop
 */
function AmenitiesInput({
  selectedAmenities,
  handleStationDetailChange,
  tooltipText,
  dynamicAmenities,
  isLoading,
}: Readonly<AmenitiesInputProperties>) {
  const { t } = useTranslation('station');
  const [openDropdown, setOpenDropdown] = useState(false);

  const dropdownReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /**
     * Close dropdown when clicking outside
     * @param {any} event - The event
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownReference.current && !dropdownReference.current.contains(event.target as Node)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="amenities-input-wrap" ref={dropdownReference}>
      <p className="amenities-label">
        {t('amenities')}
        {tooltipText && <TooltipIcon text={tooltipText} />}
      </p>

      <div
        className="amenities-input-box"
        onClick={() => setOpenDropdown((previous) => !previous)}
        role="presentation">
        {selectedAmenities.length === 0 ? (
          <span className="placeholder">{t('addAmenities')}</span>
        ) : (
          <div className="selected-amenities">
            {selectedAmenities.map((amenity) => (
              <span className="amenity-tag" key={amenity}>
                {t(formatToCamelCase(amenity))}
              </span>
            ))}
          </div>
        )}

        <div className="dropdown-icon">
          {isLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <PlusIcon />
          )}
        </div>
      </div>

      {openDropdown && (
        <div className="amenities-dropdown">
          <SelectAmenitiesV2
            selectedAmenities={selectedAmenities}
            handleStationDetailChange={handleStationDetailChange}
            dynamicAmenities={dynamicAmenities}
          />
        </div>
      )}
    </div>
  );
}

export default AmenitiesInput;
